import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/helpers/helpers';
import {
  fetchUserMessages,
  fetchSenderProfileImage,
  deleteMessagesByIds,
  massSendMessages,
  createMessage,
  fetchMembersByClubID,
  markMessagesAsRead,
} from '../../utils/api/api';
import SupabaseImageAvatar from '../../utils/components/SupabaseImageAvatar';
import { SnackbarNotification } from '../../utils/components/SnackbarNotification';
import PageLoader from '../../utils/components/PageLoader';
import { isArchivedMember } from '../../utils/memberArchiveUtils';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import { useSystemButtonStyles } from "../../hooks/useSystemButtonStyles";
import { getMm, getPageTitleSx } from "../../theme/modernMaterialEnterprise";

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Typography,
  Card,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Chip,
  Divider,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { pageContainerSx } from "../../constants/layout";
import {
  Reply as ReplyIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Message as MessageIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

export default function MessagesPage({ selectedPage, setSelectedPage, userInfo, isLoading: globalLoading, setIsLoading: setGlobalLoading }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();
  const {
    outlineButtonSx,
    iconActionSx,
    destructiveIconButtonSx,
    dialogFormPrimarySx,
    dialogFormCancelSx,
    dialogFormDestructiveSx,
  } = useSystemButtonStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentUserId = userInfo?.[0]?.studentID;
  // Use pagination for regular users; admins see all. ?paginate=1 in URL forces pagination for testing.
  const forcePaginate = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('paginate') === '1';

  // State hooks
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [openReplyDialog, setOpenReplyDialog] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [currentThreadFirstMsg, setCurrentThreadFirstMsg] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openMassSendDialog, setOpenMassSendDialog] = useState(false);
  const [massMessage, setMassMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  // Inline messaging state
  const [threadInputs, setThreadInputs] = useState({});
  const [sendingThread, setSendingThread] = useState(null);
  
  // Filter by user (for both admin and regular users)
  const [selectedUserFilter, setSelectedUserFilter] = useState('all');
  
  // Image enlargement dialog state
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [enlargedImageUser, setEnlargedImageUser] = useState(null);

  // Admin send message state
  const [openSendMessageDialog, setOpenSendMessageDialog] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [adminMessageContent, setAdminMessageContent] = useState('');
  const [sendingAdminMessage, setSendingAdminMessage] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  
  // Pagination for regular users (5 threads at a time)
  const MESSAGES_PAGE_SIZE = 5;
  const [totalThreads, setTotalThreads] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Image click handlers
  const handleImageClick = useCallback((user) => {
    setEnlargedImageUser(user);
    setImageDialogOpen(true);
  }, []);
  
  const handleCloseImageDialog = useCallback(() => {
    setImageDialogOpen(false);
    setEnlargedImageUser(null);
  }, []);

  const isAdmin = userInfo?.[0]?.isAdmin ?? false;

  const enrichMessagesWithProfiles = useCallback(async (rawMessages) => {
    if (!rawMessages || rawMessages.length === 0) return [];
    // Collect all unique participant IDs (senders + recipients)
    const uniqueIds = new Set();
    rawMessages.forEach((msg) => {
      const senderId = msg.sender?.id;
      if (senderId != null) uniqueIds.add(senderId);
      const recipients = Array.isArray(msg.recipients) ? msg.recipients : [];
      recipients.forEach((r) => {
        if (r?.id != null) uniqueIds.add(r.id);
      });
    });
    // Fetch each unique ID's profile once (avoid redundant API calls)
    const idToProfile = {};
    await Promise.all(
      Array.from(uniqueIds).map(async (id) => {
        try {
          const profile = await fetchSenderProfileImage(id);
          idToProfile[id] = profile;
        } catch (e) {
          idToProfile[id] = { name: 'Unknown', studentID: id };
        }
      })
    );
    // Enrich each message with names from the map
    return rawMessages.map((msg) => {
      const senderId = msg.sender?.id;
      const senderProfile = senderId ? idToProfile[senderId] : null;
      const recipientNames = {};
      const recipients = Array.isArray(msg.recipients) ? msg.recipients : [];
      recipients.forEach((r) => {
        if (r?.id != null && idToProfile[r.id]) {
          recipientNames[r.id] = idToProfile[r.id].name;
        }
      });
      return {
        ...msg,
        _id: msg._id || msg.id,
        senderName: senderProfile?.name || 'Unknown',
        senderStudentID: senderProfile?.studentID || senderId,
        recipientNames,
      };
    });
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!currentUserId) return;
    try {
      setIsLoading(true);
      const usePagination = !isAdmin || forcePaginate;
      const options = usePagination ? { limit: MESSAGES_PAGE_SIZE, offset: 0 } : {};
      const response = await fetchUserMessages(currentUserId, undefined, options);
      const raw = Array.isArray(response) ? response : (response?.messages || []);
      const total = response?.totalThreads ?? null;
      const withProfiles = await enrichMessagesWithProfiles(raw);
      setMessages(withProfiles);

      // Mark all messages where user is recipient and not yet read as read (user opened the page)
      const uid = parseInt(currentUserId, 10);
      const getUnreadIds = (messages) =>
        (messages || [])
          .filter((msg) => {
            const recipients = msg.recipients || [];
            const isRecipient = recipients.some((r) => r?.id != null && (parseInt(r.id, 10) === uid || r.id === uid || r.id === currentUserId));
            const readBy = msg.readBy || [];
            const alreadyRead = Array.isArray(readBy) && (readBy.includes(uid) || readBy.includes(currentUserId));
            return isRecipient && !alreadyRead;
          })
          .map((msg) => msg.id || msg._id)
          .filter(Boolean);

      let unreadIds = getUnreadIds(raw);
      // For paginated (non-admin) users: display fetch only returns first 5 threads.
      // Fetch ALL unread messages separately so we mark them all as read, not just those in the first page.
      if (usePagination) {
        try {
          const unreadResponse = await fetchUserMessages(currentUserId, false, { limit: 200 });
          const unreadRaw = Array.isArray(unreadResponse) ? unreadResponse : (unreadResponse?.messages || []);
          const allUnreadIds = getUnreadIds(unreadRaw);
          if (allUnreadIds.length > 0) {
            unreadIds = [...new Set([...unreadIds, ...allUnreadIds])];
          }
        } catch {
          // Fallback to marking only what we have from display fetch
        }
      }
      if (unreadIds.length > 0) {
        markMessagesAsRead(unreadIds, currentUserId).catch(() => {});
      }

      if (total != null) setTotalThreads(total);
    } catch (err) {
      console.error('❌ [MessagePage] Error fetching messages:', err);
      setSnackbarMessage(t('error_fetching_messages'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, isAdmin, forcePaginate, enrichMessagesWithProfiles, t]);

  const handleLoadMoreMessages = useCallback(async () => {
    const usePagination = !isAdmin || forcePaginate;
    if (!currentUserId || !usePagination || loadingMore) return;
    const threadsMap = {};
    messages.forEach((msg) => {
      const tid = msg.threadId || msg._id;
      if (!threadsMap[tid]) threadsMap[tid] = [];
      threadsMap[tid].push(msg);
    });
    const currentThreadCount = Object.keys(threadsMap).length;
    try {
      setLoadingMore(true);
      const response = await fetchUserMessages(currentUserId, undefined, {
        limit: MESSAGES_PAGE_SIZE,
        offset: currentThreadCount,
      });
      const raw = response?.messages || [];
      const total = response?.totalThreads ?? null;
      const withProfiles = await enrichMessagesWithProfiles(raw);
      const existingIds = new Set(messages.map((m) => m._id || m.id));
      const newMsgs = withProfiles.filter((m) => !existingIds.has(m._id || m.id));
      setMessages((prev) => [...prev, ...newMsgs]);
      if (total != null) setTotalThreads(total);
    } catch (err) {
      console.error('❌ [MessagePage] Error loading more messages:', err);
      setSnackbarMessage(t('error_fetching_messages'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingMore(false);
    }
  }, [currentUserId, isAdmin, messages, loadingMore, enrichMessagesWithProfiles, t, forcePaginate]);

  useEffect(() => {
    if (setGlobalLoading && selectedPage === 'MessagePage') setGlobalLoading(isLoading);
  }, [isLoading, selectedPage, setGlobalLoading]);

  useEffect(() => {
    if (selectedPage === 'MessagePage' && currentUserId) {
      setMessages([]);
      setTotalThreads(0);
      fetchMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPage, currentUserId]);

  // Fetch members when admin opens send message dialog
  useEffect(() => {
    if (openSendMessageDialog && isAdmin) {
      setLoadingMembers(true);
      setSelectedRecipient(null);
      setAdminMessageContent('');
      fetchMembersByClubID()
        .then((data) => setMembers(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error('Error fetching members:', err);
          setMembers([]);
          setSnackbarMessage(t('error_fetching_data') || 'Error fetching members');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        })
        .finally(() => setLoadingMembers(false));
    }
  }, [openSendMessageDialog, isAdmin, t]);

  const handleCloseSendMessageDialog = () => {
    if (!sendingAdminMessage) {
      setOpenSendMessageDialog(false);
      setSelectedRecipient(null);
      setAdminMessageContent('');
      setMemberSearchQuery('');
    }
  };

  const handleSendAdminMessage = async () => {
    if (!selectedRecipient || !adminMessageContent?.trim() || !currentUserId) return;
    setSendingAdminMessage(true);
    try {
      await createMessage({
        senderID: currentUserId,
        senderType: 'admin',
        recipientID: selectedRecipient.studentID,
        recipientType: 'user',
        subject: t('message_from_admin') || 'Message from admin',
        content: adminMessageContent.trim(),
      });
      setAdminMessageContent('');
      setSelectedRecipient(null);
      setOpenSendMessageDialog(false);
      fetchMessages();
      setSnackbarMessage(t('message_sent_successfully') || 'Message sent successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error sending admin message:', err);
      setSnackbarMessage(t('error_sending_message') || 'Error sending message. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSendingAdminMessage(false);
    }
  };

  const filteredMembers = members.filter((m) => {
    if (isArchivedMember(m)) return false;
    if (!memberSearchQuery?.trim()) return true;
    const name = (m.name || '').toLowerCase();
    const query = memberSearchQuery.toLowerCase().trim();
    return name.includes(query);
  });

  // Group by threadId
  const groupMessagesByThread = (list) => {
    const threads = {};
    list.forEach((msg) => {
      const tid = msg.threadId || msg._id;
      threads[tid] = threads[tid] || [];
      threads[tid].push(msg);
    });
    return threads;
  };
  
  // Group all messages first
  const allGroupedMessages = groupMessagesByThread(messages);
  
  // Get unique users from threads (for filter - works for both admin and regular users)
  // The other participant is either the sender (when they sent to us) or in recipients (when we sent to them)
  const getUniqueUsers = () => {
    const userMap = new Map();
    Object.values(allGroupedMessages).forEach((thread) => {
      if (thread && thread.length > 0) {
        let otherUser = null;
        for (const msg of thread) {
          // Other as sender (message from them to us)
          if (msg.sender && msg.sender.id && msg.sender.id !== currentUserId) {
            otherUser = {
              id: msg.sender.id,
              name: msg.senderName || msg.sender?.name || 'Unknown',
              studentID: msg.senderStudentID || msg.sender.id
            };
            break;
          }
          // Other in recipients (message from us to them)
          const recipients = Array.isArray(msg.recipients) ? msg.recipients : [];
          const otherRecipient = recipients.find(r => r && r.id && r.id !== currentUserId);
          if (otherRecipient) {
            // Use recipientNames from enrichment, or name from thread where they were sender
            const nameFromEnrichment = msg.recipientNames?.[otherRecipient.id];
            const nameFromThread = thread.find(m => m.sender?.id === otherRecipient.id)?.senderName;
            otherUser = {
              id: otherRecipient.id,
              name: nameFromEnrichment || otherRecipient.name || nameFromThread || 'Unknown',
              studentID: otherRecipient.studentID || otherRecipient.id
            };
            break;
          }
        }
        if (otherUser && !userMap.has(otherUser.id)) {
          userMap.set(otherUser.id, otherUser);
        }
      }
    });
    return Array.from(userMap.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  };
  
  // Filter threads by selected user (works for both admin and regular users)
  const filteredGroupedMessages = selectedUserFilter !== 'all'
    ? Object.fromEntries(
        Object.entries(allGroupedMessages).filter(([threadId, thread]) => {
          const matchId = (id) => id != null && String(id) === String(selectedUserFilter);
          return thread.some(msg => {
            // Other participant as sender
            if (msg.sender && msg.sender.id !== currentUserId && matchId(msg.sender.id)) return true;
            // Other participant in recipients
            const recipients = Array.isArray(msg.recipients) ? msg.recipients : [];
            return recipients.some(r => r && matchId(r.id));
          });
        })
      )
    : allGroupedMessages;
  
  const groupedMessages = filteredGroupedMessages;

  // Handlers
  const handleSelectMessage = (id) => {
    setSelectedMessages((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openThreadReply = (firstMsg) => {
    setCurrentThreadFirstMsg(firstMsg);
    setOpenReplyDialog(true);
  };

  const handleSendReply = async () => {
    if (!currentThreadFirstMsg) return;
    try {
      await createMessage({
        senderID: currentUserId,
        senderType: 'user',
        recipientID: currentThreadFirstMsg.sender.id,
        recipientType: 'user',
        subject: 'Reply',
        content: replyMessage,
        threadId: currentThreadFirstMsg.threadId,
      });
      await fetchMessages();
      setReplyMessage('');
      setOpenReplyDialog(false);
      setSnackbarMessage(t('reply_sent_successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error sending reply:', err);
      setSnackbarMessage(t('error_sending_reply'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleOpenDeleteDialog = () => setOpenDeleteDialog(true);
  const handleCloseDeleteDialog = () => setOpenDeleteDialog(false);

  // Delete whole conversation (admin only)
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [deletingConversation, setDeletingConversation] = useState(false);
  const handleOpenDeleteConversation = (threadId) => setConversationToDelete(threadId);
  const handleCloseDeleteConversation = () => { if (!deletingConversation) setConversationToDelete(null); };
  const handleConfirmDeleteConversation = async () => {
    if (!conversationToDelete || !isAdmin) return;
    const thread = allGroupedMessages[conversationToDelete];
    if (!thread || thread.length === 0) return;
    const messageIds = thread.map((m) => m._id || m.id).filter(Boolean);
    if (messageIds.length === 0) return;
    setDeletingConversation(true);
    try {
      await deleteMessagesByIds(messageIds);
      await fetchMessages();
      setConversationToDelete(null);
      setSnackbarMessage(t('conversation_deleted_successfully') || 'Conversation deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setSnackbarMessage(t('error_deleting_messages') || 'Error deleting conversation');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeletingConversation(false);
    }
  };

  const handleDeleteMessages = async () => {
    try {
      // Only delete messages that can actually be deleted
      const deletableSelectedMessages = selectedMessages.filter(id => {
        const message = messages.find(msg => msg._id === id);
        return message && canDeleteMessage(message);
      });
      
      if (deletableSelectedMessages.length === 0) {
        setSnackbarMessage(t('no_deletable_messages'));
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        handleCloseDeleteDialog();
        return;
      }
      
      await deleteMessagesByIds(deletableSelectedMessages);
      await fetchMessages();
      setSelectedMessages([]);
      setSnackbarMessage(t('messages_deleted_successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error deleting:', err);
      setSnackbarMessage(t('error_deleting_messages'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      handleCloseDeleteDialog();
    }
  };



  const handleOpenMassSendDialog = () => setOpenMassSendDialog(true);
  const handleCloseMassSendDialog = () => setOpenMassSendDialog(false);

  const handleMassSendMessage = async () => {
    try {
      await massSendMessages({
        senderID: currentUserId,
        senderType: 'user',
        recipients: selectedMessages.map((id) => ({ id, type: 'user' })),
        subject: 'Mass Message',
        content: massMessage,
      });
      await fetchMessages();
      setSelectedMessages([]);
      setMassMessage('');
      setSnackbarMessage(t('mass_message_sent_successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error mass sending:', err);
      setSnackbarMessage(t('error_sending_mass_message'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      handleCloseMassSendDialog();
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Message deletion logic: Admin can delete any message; regular user can only delete own messages within 5 minutes
  const canDeleteMessage = (message) => {
    if (!message?.sender?.id) return false;
    // Admin can always delete any message
    if (isAdmin) return true;
    // Regular user: only their own messages within 5 minutes
    const isOwner = String(message.sender.id) === String(currentUserId);
    if (!isOwner) return false;
    const messageTime = new Date(message.createdAt || message.created_at);
    const now = new Date();
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
    return messageTime > fiveMinutesAgo;
  };

  const getDeletableMessages = (messages) => {
    return messages.filter(msg => canDeleteMessage(msg));
  };

  // Inline messaging handlers
  const handleThreadInputChange = (threadId, value) => {
    setThreadInputs(prev => ({
      ...prev,
      [threadId]: value
    }));
  };

  const handleSendInlineMessage = async (threadId, firstMsg) => {
    const message = threadInputs[threadId]?.trim();
    if (!message) return;

    try {
      setSendingThread(threadId);
      await createMessage({
        senderID: currentUserId,
        senderType: 'user',
        recipientID: firstMsg.sender.id,
        recipientType: 'user',
        subject: 'Reply',
        content: message,
        threadId: firstMsg.threadId,
      });
      await fetchMessages();
      setThreadInputs(prev => ({
        ...prev,
        [threadId]: ''
      }));
      setSnackbarMessage(t('reply_sent_successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error sending inline reply:', err);
      setSnackbarMessage(t('error_sending_reply'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSendingThread(null);
    }
  };

  // Create user object for SupabaseImageAvatar
  const getSenderUser = (msg) => {
    return {
      studentID: msg.senderStudentID,
      name: msg.senderName,
    };
  };

  /** Other participant in the thread (who you're talking with), not always the first message sender */
  const getThreadPartnerUser = (threadMessages) => {
    for (const msg of threadMessages) {
      if (msg?.sender?.id != null && String(msg.sender.id) !== String(currentUserId)) {
        return {
          studentID: msg.senderStudentID || msg.sender.id,
          name: msg.senderName || msg.sender?.name || '—',
        };
      }
      const recs = Array.isArray(msg.recipients) ? msg.recipients : [];
      const other = recs.find((r) => r?.id != null && String(r.id) !== String(currentUserId));
      if (other) {
        return {
          studentID: other.studentID || other.id,
          name: msg.recipientNames?.[other.id] || other.name || '—',
        };
      }
    }
    return getSenderUser(threadMessages[0]);
  };

  const getThreadPartnerDisplay = (threadMessages) => getThreadPartnerUser(threadMessages).name;

  const getRecipientLine = (msg) => {
    const recs = Array.isArray(msg.recipients) ? msg.recipients : [];
    const parts = recs
      .map((r) => {
        if (!r?.id) return null;
        if (String(r.id) === String(currentUserId)) return t('message_you');
        return msg.recipientNames?.[r.id] || r.name || String(r.id);
      })
      .filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  };

  const pageRootSx = {
    ...pageContainerSx,
    p: { xs: 1.5, sm: 2 },
    minWidth: 0,
    pb: { xs: 12, sm: 4 },
    color: getMm(isDarkMode).onSurface,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  };

  const cardShellSx = {
    borderRadius: '8px',
    backgroundColor: getMm(isDarkMode).surfaceLowest,
    border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
    boxShadow: 'none',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  };

  const filterSelectSx = {
    backgroundColor: getMm(isDarkMode).surfaceLow,
    color: getMm(isDarkMode).onSurface,
    borderRadius: '8px',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: getMm(isDarkMode).outlineVariant,
      borderWidth: '2px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: getMm(isDarkMode).onSurface,
    },
    '& .MuiSelect-icon': { color: getMm(isDarkMode).onSurfaceVariant },
  };

  const filterMenuProps = {
    PaperProps: {
      sx: {
        backgroundColor: getMm(isDarkMode).surfaceLowest,
        border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
        borderRadius: '8px',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      },
    },
  };

  const pageHeader = (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{ ...getPageTitleSx(isDarkMode), mb: 0.25 }}
      >
        {t('messages')}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.875rem',
          color: getMm(isDarkMode).onSurfaceVariant,
          fontWeight: 500,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        {t('manage_your_conversations')}
      </Typography>
    </Box>
  );

  const toolbarRow = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        mb: 2,
      }}
    >
      {getUniqueUsers().length > 0 && (
        <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 220, maxWidth: 320, flex: isMobile ? 1 : '0 1 auto' }}>
          <InputLabel id="user-filter-label" sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {t('filter_by_user') || 'Filter by User'}
          </InputLabel>
          <Select
            labelId="user-filter-label"
            value={selectedUserFilter}
            onChange={(e) => setSelectedUserFilter(e.target.value)}
            label={t('filter_by_user') || 'Filter by User'}
            sx={filterSelectSx}
            MenuProps={filterMenuProps}
          >
            <MenuItem value="all">{t('all_users') || 'All Users'}</MenuItem>
            {getUniqueUsers().map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {userInfo?.[0]?.isAdmin && (
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => setOpenSendMessageDialog(true)}
          sx={{ ...dialogFormPrimarySx, alignSelf: isMobile ? 'stretch' : 'flex-start', flexShrink: 0 }}
        >
          {t('send_message')}
        </Button>
      )}
    </Box>
  );

  // Early return
  if (selectedPage !== 'MessagePage' || !currentUserId) {
    return null;
  }

  if (isLoading) {
    return (
      <Box sx={pageRootSx}>
        {pageHeader}
        <PageLoader message={t("loading_text") || "Loading..."} />
      </Box>
    );
  }

  return (
    <Box sx={pageRootSx}>
      {pageHeader}
      {toolbarRow}

      {!messages.length ? (
        <Card sx={{ ...cardShellSx, py: 4, px: 2, textAlign: 'center' }}>
          <MessageIcon sx={{ fontSize: 48, color: getMm(isDarkMode).onSurfaceVariant, mb: 1.5 }} />
          <Typography sx={{ fontSize: '0.875rem', color: getMm(isDarkMode).onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {t('no_messages')}
          </Typography>
        </Card>
      ) : (
        <>

          {/* Messages Threads */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            {Object.entries(groupedMessages)
              .map(([threadId, thread]) => {
                // Filter out any undefined/null messages and ensure a date exists
                const validThread = thread.filter(msg => msg && (msg.createdAt || msg.created_at));
                if (validThread.length === 0) return null; // Skip empty threads
                
                const sorted = [...validThread].sort(
                  (a, b) => {
                    const dateA = (a?.createdAt || a?.created_at) ? new Date(a.createdAt || a.created_at) : new Date(0);
                    const dateB = (b?.createdAt || b?.created_at) ? new Date(b.createdAt || b.created_at) : new Date(0);
                    return dateA - dateB; // Oldest first, newest last (iPhone/Android style)
                  }
                );
                const firstMsg = sorted[0]; // Get the oldest message (first in thread chronologically)
                const lastMsg = sorted[sorted.length - 1]; // Get the most recent message (newest)
                const unreadCount = sorted.filter(msg => msg && !msg.isRead && msg.sender?.id !== currentUserId).length;
                
                return {
                  threadId,
                  lastMsg,
                  firstMsg,
                  unreadCount,
                  sorted,
                  threadComponent: (
                  <Paper
                    key={threadId}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: getMm(isDarkMode).surfaceLowest,
                      border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                      boxShadow: 'none',
                      overflow: 'hidden',
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    }}
                  >
                    {/* Thread Header */}
                    <Box sx={{
                      backgroundColor: getMm(isDarkMode).surfaceLowest,
                      p: { xs: 1.5, sm: 2 },
                      borderBottom: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                      position: 'relative'
                    }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          flex: 1,
                          minWidth: 0
                        }}>
                          <Box
                            onClick={() => handleImageClick(getThreadPartnerUser(sorted))}
                            sx={{
                              cursor: 'pointer',
                              display: 'inline-block',
                              flexShrink: 0
                            }}
                          >
                            <SupabaseImageAvatar 
                              user={getThreadPartnerUser(sorted)} 
                              size={isMobile ? 48 : 56}
                              sx={{ 
                                border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                                boxShadow: 'none'
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                              <Typography sx={{ 
                                fontSize: isMobile ? '1.0625rem' : '1.25rem',
                                fontWeight: 700, 
                                color: getMm(isDarkMode).onSurface,
                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                                lineHeight: 1.3,
                              }}>
                                {t('message_thread_with', { name: getThreadPartnerDisplay(sorted) })}
                              </Typography>
                              {unreadCount > 0 && (
                                <Chip
                                  label={t('message_unread_badge', { count: unreadCount })}
                                  size="small"
                                  sx={{
                                    height: 26,
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    backgroundColor: isDarkMode ? '#2563EB' : '#DBEAFE',
                                    color: isDarkMode ? '#EFF6FF' : '#1D4ED8',
                                    border: isDarkMode ? '1px solid #3B82F6' : '1px solid #93C5FD',
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                  }}
                                />
                              )}
                            </Box>
                            <Typography sx={{ 
                              fontSize: '0.9375rem',
                              color: getMm(isDarkMode).onSurfaceVariant,
                              fontFamily: "'Plus Jakarta Sans', sans-serif",
                              fontWeight: 500,
                            }}>
                              {formatDateTime(lastMsg.createdAt || lastMsg.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          flexShrink: 0
                        }}>
                          {isAdmin && (
                            <Tooltip title={t('delete_conversation') || 'Delete conversation'}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDeleteConversation(threadId)}
                                sx={destructiveIconButtonSx}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Messages - Admin Dashboard Style */}
                    <Box sx={{ 
                      p: { xs: 2, sm: 2.5, md: 3 },
                      backgroundColor: getMm(isDarkMode).surfaceLowest
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {sorted.map((msg) => {
                          const owner = msg.sender.id === currentUserId;
                          return (
                            <Box
                              key={msg._id}
                              sx={{
                                display: 'flex',
                                justifyContent: owner ? 'flex-end' : 'flex-start',
                                mb: 2,
                                alignItems: 'flex-end'
                              }}
                            >
                              <Box sx={{
                                maxWidth: isMobile ? '92%' : '78%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: owner ? 'flex-end' : 'flex-start'
                              }}>
                                {/* From / To — easy to scan */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                    mb: 0.75,
                                    px: 0.5,
                                    maxWidth: '100%',
                                    flexDirection: 'row',
                                  }}
                                >
                                  <Box
                                    onClick={() => handleImageClick(getSenderUser(msg))}
                                    sx={{ cursor: 'pointer', flexShrink: 0, mt: 0.15 }}
                                  >
                                    <SupabaseImageAvatar
                                      user={getSenderUser(msg)}
                                      size={isMobile ? 28 : 32}
                                      sx={{
                                        flexShrink: 0,
                                        border: isDarkMode ? '2px solid #4B5563' : '2px solid #fff',
                                        boxShadow: 'none',
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    component="div"
                                    sx={{
                                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                                      lineHeight: 1.55,
                                      color: getMm(isDarkMode).onSurfaceVariant,
                                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                                      textAlign: owner ? 'right' : 'left',
                                    }}
                                  >
                                    {owner ? (
                                      <>
                                        <Box component="span" sx={{ fontWeight: 800, color: getMm(isDarkMode).onSurfaceVariant, mr: 0.5 }}>
                                          {t('message_label_to')}
                                        </Box>
                                        <Box component="span" sx={{ fontWeight: 700, color: getMm(isDarkMode).onSurface }}>
                                          {getRecipientLine(msg)}
                                        </Box>
                                      </>
                                    ) : (
                                      <>
                                        <Box component="span" sx={{ fontWeight: 800, color: getMm(isDarkMode).onSurfaceVariant, mr: 0.5 }}>
                                          {t('message_label_from')}
                                        </Box>
                                        <Box component="span" sx={{ fontWeight: 700, color: getMm(isDarkMode).onSurface }}>
                                          {msg.senderName || '—'}
                                        </Box>
                                      </>
                                    )}
                                  </Typography>
                                </Box>

                                {/* Message Bubble - same colors as before */}
                                <Paper
                                  sx={{
                                    backgroundColor: owner 
                                      ? '#374151'
                                      : (getMm(isDarkMode).outlineVariant),
                                    color: owner ? 'white' : (getMm(isDarkMode).onSurface),
                                    p: { xs: 1.75, sm: 2.25 },
                                    borderRadius: '8px',
                                    boxShadow: 'none',
                                    position: 'relative',
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    width: '100%',
                                  }}
                                >
                                  <Typography sx={{ 
                                    whiteSpace: 'pre-wrap',
                                    fontSize: { xs: '1rem', sm: '1.0625rem' },
                                    lineHeight: 1.55,
                                    wordBreak: 'break-word',
                                    fontStyle: msg.isDeleted ? 'italic' : 'normal',
                                    opacity: msg.isDeleted ? 0.6 : 1,
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    letterSpacing: '0.01em',
                                  }}>
                                    {msg.isDeleted ? t('this_message_was_deleted') : msg.content}
                                  </Typography>
                                </Paper>
                                
                                {/* Message Footer */}
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  mt: 1,
                                  px: 0.5,
                                  width: '100%'
                                }}>
                                  <Typography sx={{
                                    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                    color: getMm(isDarkMode).onSurfaceVariant,
                                    fontWeight: 600,
                                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                                  }}>
                                    {formatDateTime(msg.createdAt || msg.created_at)}
                                  </Typography>
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {canDeleteMessage(msg) && (
                                      <Checkbox
                                        size="small"
                                        checked={selectedMessages.includes(msg._id)}
                                        onChange={() => handleSelectMessage(msg._id)}
                                        sx={{
                                          color: isDarkMode ? '#374151' : '#374151',
                                          '&.Mui-checked': {
                                            color: isDarkMode ? '#374151' : '#374151'
                                          }
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    {/* Inline Reply Input */}
                    <Box sx={{
                      p: { xs: 1.5, sm: 2 },
                      backgroundColor: getMm(isDarkMode).surfaceLowest,
                      borderTop: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 1, sm: 2 }
                    }}>
                      <TextField
                        fullWidth
                        placeholder={t('type_your_reply')}
                        value={threadInputs[threadId] || ''}
                        onChange={(e) => handleThreadInputChange(threadId, e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: getMm(isDarkMode).surfaceLowest,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            '& fieldset': {
                              borderColor: getMm(isDarkMode).outlineVariant,
                              borderWidth: '2px'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: getMm(isDarkMode).onSurface
                            },
                            '& input': {
                              color: getMm(isDarkMode).onSurface,
                              fontFamily: "'Plus Jakarta Sans', sans-serif"
                            },
                            '& .MuiInputBase-input::placeholder': {
                              color: getMm(isDarkMode).onSurfaceVariant,
                              fontFamily: "'Plus Jakarta Sans', sans-serif"
                            }
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendInlineMessage(threadId, firstMsg);
                          }
                        }}
                      />
                      <IconButton
                        onClick={() => handleSendInlineMessage(threadId, firstMsg)}
                        disabled={!threadInputs[threadId]?.trim() || sendingThread === threadId}
                        sx={iconActionSx}
                      >
                        {sendingThread === threadId ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SendIcon />
                        )}
                      </IconButton>
                  </Box>
                </Paper>
                  )
                };
              })
              .filter(item => item !== null) // Remove null entries
              .sort((a, b) => {
                const dateA = (a?.lastMsg?.createdAt || a?.lastMsg?.created_at) ? new Date(a.lastMsg.createdAt || a.lastMsg.created_at) : new Date(0);
                const dateB = (b?.lastMsg?.createdAt || b?.lastMsg?.created_at) ? new Date(b.lastMsg.createdAt || b.lastMsg.created_at) : new Date(0);
                return dateB - dateA; // Newest threads first
              })
              .map(item => item.threadComponent) // Extract the component
            }
          </Box>

          {/* Load More - Regular users (or ?paginate=1), when more threads exist */}
          {(!isAdmin || forcePaginate) && totalThreads > 0 && (() => {
            const threadsMap = {};
            messages.forEach((msg) => {
              const tid = msg.threadId || msg._id;
              if (!threadsMap[tid]) threadsMap[tid] = [];
              threadsMap[tid].push(msg);
            });
            const currentThreadCount = Object.keys(threadsMap).length;
            const hasMore = currentThreadCount < totalThreads;
            if (!hasMore) return null;
            return (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2, px: { xs: 1, sm: 0 } }}>
                <Button variant="outlined"
                  onClick={handleLoadMoreMessages}
                  disabled={loadingMore}
                  fullWidth={isMobile}
                  sx={outlineButtonSx}
                >
                  {loadingMore ? (
                    <CircularProgress size={24} sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                  ) : (
                    t('load_more')
                  )}
                </Button>
              </Box>
            );
          })()}

          {/* Action Buttons - Admin Dashboard Style */}
          {selectedMessages.length > 0 && getDeletableMessages(messages.filter(msg => selectedMessages.includes(msg._id))).length > 0 && (
            <Paper sx={{
              position: 'fixed',
              bottom: { xs: 80, sm: 20 },
              left: { xs: 8, sm: '50%' },
              right: { xs: 8, sm: 'auto' },
              transform: { xs: 'none', sm: 'translateX(-50%)' },
              borderRadius: '8px',
              backgroundColor: getMm(isDarkMode).surfaceLowest,
              border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
              boxShadow: 'none',
              zIndex: 1000,
              p: { xs: 1.5, sm: 2 },
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              maxWidth: { xs: 'none', sm: 400 }
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? 1 : 2,
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}>
                <Chip
                  label={`${selectedMessages.length} ${t('selected')}`}
                  sx={{
                    backgroundColor: isDarkMode ? '#374151' : '#374151',
                    color: 'white',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    boxShadow: 'none',
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}
                />
                
                <Button variant="contained" color="error"
                 
                  startIcon={<DeleteIcon />}
                  onClick={handleOpenDeleteDialog}
                  sx={dialogFormDestructiveSx}
                >
                  {t('delete')}
                </Button>
              </Box>
            </Paper>
          )}

        </>
      )}

          {/* Reply Dialog - Admin Dashboard Style */}
          <Dialog
            open={openReplyDialog}
            onClose={() => setOpenReplyDialog(false)}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '8px',
                boxShadow: 'none',
                backgroundColor: getMm(isDarkMode).surfaceLowest,
                border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }
            }}
          >
            <DialogTitle sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              color: getMm(isDarkMode).onSurface,
              pb: 1,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span>{t('reply_to_message')}</span>
              {isMobile && (
                <IconButton onClick={() => setOpenReplyDialog(false)} sx={{ color: 'inherit', mr: -1 }}>
                  <CloseIcon />
                </IconButton>
              )}
            </DialogTitle>
            <DialogContent sx={{ 
              pt: 3,
              p: { xs: 2, sm: 3 },
              backgroundColor: getMm(isDarkMode).surfaceLowest,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              <DialogContentText sx={{ 
                color: getMm(isDarkMode).onSurfaceVariant, 
                fontSize: '0.875rem',
                lineHeight: 1.6,
                mb: 2,
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {t('enter_reply')}
              </DialogContentText>
              <TextField
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                placeholder={t('type_your_reply')}
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: getMm(isDarkMode).surfaceLowest,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    '& fieldset': {
                      borderColor: getMm(isDarkMode).outlineVariant,
                      borderWidth: '2px'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: getMm(isDarkMode).onSurface
                    },
                    '& textarea': {
                      color: getMm(isDarkMode).onSurface,
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: getMm(isDarkMode).onSurfaceVariant,
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    }
                  }
                }}
              />
            </DialogContent>
            <DialogActions sx={{ 
              p: { xs: 2, sm: 2 }, 
              gap: 1.5,
              flexDirection: isMobile ? 'column-reverse' : 'row',
              backgroundColor: getMm(isDarkMode).surfaceLowest,
              borderTop: `1px solid ${getMm(isDarkMode).outlineVariant}`
            }}>
              <Button variant="outlined"
                onClick={() => setOpenReplyDialog(false)}
                fullWidth={isMobile}
               
                sx={dialogFormCancelSx}
              >
                {t('cancel')}
              </Button>
              <Button variant="contained"
                onClick={handleSendReply}
                startIcon={<SendIcon />}
                fullWidth={isMobile}
                sx={dialogFormPrimarySx}
              >
                {t('send')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Dialog - Admin Dashboard Style */}
          <Dialog
            open={openDeleteDialog}
            onClose={handleCloseDeleteDialog}
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                borderRadius: '8px',
                boxShadow: 'none',
                backgroundColor: getMm(isDarkMode).surfaceLowest,
                border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }
            }}
          >
        <DialogTitle sx={{
          fontWeight: 600,
          fontSize: '1.125rem',
          color: getMm(isDarkMode).onSurface,
          pb: 1,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>{t('delete_messages')}</span>
          {isMobile && (
            <IconButton onClick={handleCloseDeleteDialog} sx={{ color: 'inherit', mr: -1 }}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
            <DialogContent sx={{ 
              pt: 3,
              p: 3,
              backgroundColor: getMm(isDarkMode).surfaceLowest,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              <DialogContentText sx={{ 
                color: getMm(isDarkMode).onSurfaceVariant, 
                fontSize: '0.875rem',
                lineHeight: 1.6,
                mb: 2,
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {t('delete_confirmation')}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ 
              p: 3, 
              gap: 2,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              <Button variant="outlined"
                onClick={handleCloseDeleteDialog}
               
                sx={dialogFormCancelSx}
              >
                {t('cancel')}
              </Button>
              <Button variant="contained" color="error"
                onClick={handleDeleteMessages}
               
                startIcon={<DeleteIcon />}
                sx={dialogFormDestructiveSx}
              >
                {t('delete')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Conversation Dialog - Admin only */}
          <Dialog
            open={!!conversationToDelete}
            onClose={handleCloseDeleteConversation}
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                borderRadius: '8px',
                boxShadow: 'none',
                backgroundColor: getMm(isDarkMode).surfaceLowest,
                border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }
            }}
          >
            <DialogTitle sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              color: getMm(isDarkMode).onSurface,
              pb: 1,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span>{t('delete_conversation') || 'Delete conversation'}</span>
              {isMobile && (
                <IconButton onClick={handleCloseDeleteConversation} sx={{ color: 'inherit', mr: -1 }}>
                  <CloseIcon />
                </IconButton>
              )}
            </DialogTitle>
            <DialogContent sx={{ 
              pt: 3,
              p: 3,
              backgroundColor: getMm(isDarkMode).surfaceLowest,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              <DialogContentText sx={{ 
                color: getMm(isDarkMode).onSurfaceVariant, 
                fontSize: '0.875rem',
                lineHeight: 1.6,
                mb: 2,
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {t('delete_conversation_confirm') || 'Are you sure you want to delete this entire conversation? This action cannot be undone.'}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ 
              p: 3, 
              gap: 2,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              <Button variant="outlined"
                onClick={handleCloseDeleteConversation}
                disabled={deletingConversation}
               
                sx={dialogFormCancelSx}
              >
                {t('cancel')}
              </Button>
              <Button variant="contained" color="error"
                onClick={handleConfirmDeleteConversation}
                disabled={deletingConversation}
               
                startIcon={deletingConversation ? <CircularProgress size={20} /> : <DeleteIcon />}
                sx={dialogFormDestructiveSx}
              >
                {deletingConversation ? (t('deleting') || 'Deleting...') : t('delete')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Mass Send Dialog - Admin Dashboard Style */}
          <Dialog
            open={openMassSendDialog}
            onClose={handleCloseMassSendDialog}
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                borderRadius: '8px',
                boxShadow: 'none',
                backgroundColor: getMm(isDarkMode).surfaceLowest,
                border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }
            }}
          >
            <DialogTitle sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              color: getMm(isDarkMode).onSurface,
              pb: 1,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span>{t('send_message_to_all')}</span>
              {isMobile && (
                <IconButton onClick={handleCloseMassSendDialog} sx={{ color: 'inherit', mr: -1 }}>
                  <CloseIcon />
                </IconButton>
              )}
            </DialogTitle>
            <DialogContent sx={{ 
              pt: 3,
              p: 3,
              backgroundColor: getMm(isDarkMode).surfaceLowest,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              <DialogContentText sx={{ 
                color: getMm(isDarkMode).onSurfaceVariant, 
                fontSize: '0.875rem',
                lineHeight: 1.6,
                mb: 2,
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {t('enter_mass_message')}
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                label={t('message')}
                fullWidth
                variant="outlined"
                value={massMessage}
                onChange={(e) => setMassMessage(e.target.value)}
                multiline
                rows={4}
                placeholder={t('type_your_message')}
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: getMm(isDarkMode).surfaceLowest,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    '& fieldset': {
                      borderColor: getMm(isDarkMode).outlineVariant,
                      borderWidth: '2px'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: getMm(isDarkMode).onSurface
                    },
                    '& textarea': {
                      color: getMm(isDarkMode).onSurface,
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    },
                    '& label': {
                      color: getMm(isDarkMode).onSurfaceVariant,
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: getMm(isDarkMode).onSurfaceVariant,
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    }
                  }
                }}
              />
            </DialogContent>
            <DialogActions sx={{ 
              p: 3, 
              gap: 2,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              <Button variant="outlined"
                onClick={handleCloseMassSendDialog}
               
                sx={dialogFormCancelSx}
              >
                {t('cancel')}
              </Button>
              <Button variant="contained"
                onClick={handleMassSendMessage}
                startIcon={<SendIcon />}
                sx={dialogFormPrimarySx}
              >
                {t('send')}
              </Button>
            </DialogActions>
          </Dialog>

          <SnackbarNotification
            open={snackbarOpen}
            onClose={handleSnackbarClose}
            message={snackbarMessage}
            severity={snackbarSeverity}
          />

      {/* Admin Send Message Dialog */}
      <Dialog
        open={openSendMessageDialog}
        onClose={handleCloseSendMessageDialog}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            backgroundColor: getMm(isDarkMode).surfaceLowest,
            border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: getMm(isDarkMode).onSurface, 
          fontWeight: 600, 
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          borderBottom: `1px solid ${getMm(isDarkMode).outlineVariant}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>{t('send_message')}</span>
          {isMobile && (
            <IconButton onClick={handleCloseSendMessageDialog} sx={{ color: 'inherit', mr: -1 }}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, p: { xs: 2, sm: 3 } }}>
          {loadingMembers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: isDarkMode ? '#374151' : '#374151' }} />
            </Box>
          ) : (
            <>
              <Typography sx={{ 
                fontSize: '0.875rem', 
                fontWeight: 600, 
                color: getMm(isDarkMode).onSurfaceVariant, 
                mb: 1.5,
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {t('select_recipient') || 'Select recipient'}
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder={t('search_members') || 'Search members...'}
                value={memberSearchQuery}
                onChange={(e) => setMemberSearchQuery(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: getMm(isDarkMode).surfaceLow,
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }
                }}
              />
              <Box sx={{ 
                maxHeight: { xs: '40vh', sm: 280 }, 
                overflowY: 'auto', 
                border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                borderRadius: '8px',
                p: 1
              }}>
                {filteredMembers
                  .filter((m) => m.studentID && m.studentID !== currentUserId)
                  .map((member) => (
                    <Box
                      key={member.studentID}
                      onClick={() => setSelectedRecipient(member)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: selectedRecipient?.studentID === member.studentID
                          ? (getMm(isDarkMode).outlineVariant)
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: getMm(isDarkMode).surfaceContainer
                        }
                      }}
                    >
                      <SupabaseImageAvatar
                        user={member}
                        size={48}
                        sx={{ flexShrink: 0 }}
                      />
                      <Typography sx={{ 
                        fontSize: '1rem', 
                        fontWeight: 500, 
                        color: getMm(isDarkMode).onSurface,
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}>
                        {member.name || t('unknown')}
                      </Typography>
                    </Box>
                  ))}
              </Box>

              {selectedRecipient && (
                <Box sx={{ mt: 3 }}>
                  <Typography sx={{ 
                    fontSize: '0.875rem', 
                    fontWeight: 600, 
                    color: getMm(isDarkMode).onSurfaceVariant, 
                    mb: 1.5,
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}>
                    {t('message_to_recipient') || 'Message to'} {selectedRecipient.name}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={adminMessageContent}
                    onChange={(e) => setAdminMessageContent(e.target.value)}
                    placeholder={t('message_placeholder') || 'Write your message here...'}
                    disabled={sendingAdminMessage}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: getMm(isDarkMode).surfaceLow,
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCloseSendMessageDialog}
            disabled={sendingAdminMessage}
            sx={dialogFormCancelSx}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendAdminMessage}
            disabled={sendingAdminMessage || !selectedRecipient || !adminMessageContent?.trim()}
            startIcon={<SendIcon />}
            sx={dialogFormPrimarySx}
          >
            {sendingAdminMessage ? (t('sending') || 'Sending...') : t('send_message')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Image Enlargement Dialog - Admin Dashboard Style */}
      <Dialog
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: 'none',
            margin: 0,
            width: 'auto',
            height: 'auto',
            overflow: 'visible',
            pointerEvents: 'none'
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          },
          onClick: handleCloseImageDialog
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto'
          }
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: 'relative',
            display: 'inline-flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'auto'
          }}
        >
          {enlargedImageUser && (
            <>
              <Box sx={{ position: 'relative' }}>
              <SupabaseImageAvatar
                user={enlargedImageUser}
                size={600}
                sx={{
                  border: `4px solid ${isDarkMode ? '#ffffff' : '#ffffff'}`,
                  boxShadow: 'none',
                  borderRadius: '50%',
                  width: { xs: '90vw', sm: '350px', md: '400px' },
                  height: { xs: '90vw', sm: '350px', md: '400px' },
                  maxWidth: { xs: '600px', sm: '350px', md: '400px' },
                  maxHeight: { xs: '600px', sm: '350px', md: '400px' }
                }}
              />
              <IconButton
                onClick={handleCloseImageDialog}
                sx={{
                  position: 'absolute',
                  top: { xs: -8, sm: -12 },
                  right: { xs: -8, sm: -12 },
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: '#ffffff',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
              </IconButton>
              </Box>
              {enlargedImageUser.name && (
                <Typography sx={{ mt: 2, color: '#ffffff', fontWeight: 600, fontSize: '1.25rem', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {enlargedImageUser.name}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
